// ============================================================================
// Vendor Dashboard — Summary tab. Three figures over exactly the rows the Trips
// tab is showing, so the two tabs can never disagree: the same `visibleRows` the
// page has already filtered (date window, city, warehouse, express, vendor,
// driver, search) is what is counted here. Those rows are trips-only — the page
// asks the server for `has_trip`, so nothing untrip-assigned reaches these tiles.
//
// Three tiles, one shape each: the status slice it counts (all / delivered / not
// yet delivered), then the SAME two breakdowns under it —
//   - by MILE, as a part-to-whole bar with direct labels (identity → categorical)
//   - by SLA, as At risk | Breached in the footer
// Repeating both on every tile is what lets you read across: "of the 12 still
// out, how many are first mile, and how many are late?" is one glance, not three.
//
// Counted PER SHIPMENT (one row = one booking = one truck movement), not per
// distinct order: a consolidated truck carrying three POs is one shipment here,
// and the drill-down lists it once.
//
// Every figure drills down, and they COMPOSE: a click sends
// {metric, mile?, sla?} and the drawer lists exactly that intersection — so
// "At risk" inside the Delivered tile lists the delivered-and-at-risk shipments,
// not every at-risk one. See `rowsForRequest`, which both the tile counts and the
// drawer read, so a number can never disagree with the list behind it.
// ============================================================================
import { CheckCircleFilled, ClockCircleFilled } from "@ant-design/icons";
import { Col, Row, Tooltip } from "antd";
import React from "react";
import DriverPerformanceSection from "./driver_performance_tile";
import { DriverPerformance } from "./driver_performance";
import { CarrierSummaryResponse } from "./carrier.api";
import styles from "./summary.module.scss";
import { LogisticsOrderRow } from "../types";
import { isDelivered, isFirstMile } from "./row_predicates";
import { breachOf } from "./breach";

// The status slice a tile stands for.
export type SummaryMetric = "shipments" | "delivered" | "incomplete";
export type MileSlice = "first_mile" | "last_mile";
export type SlaSlice = "at_risk" | "breached";

// What a clicked figure asks for: a status slice, optionally narrowed by mile and/or SLA.
export interface SummaryRequest {
  metric: SummaryMetric;
  mile?: MileSlice;
  sla?: SlaSlice;
}

// Re-exported from `row_predicates`, which owns them — the SLA rule needs the same two tests, and
// defining them here would make this module and `breach` import each other.
export { isDelivered, isFirstMile };

// --- SLA ------------------------------------------------------------------------------------
// BREACHED is live — see `breach.ts` for the four rules and why a missing input yields "unknown"
// rather than a verdict, and `escalation_excuse.ts` for the escalations that drop a late shipment
// out of this count entirely. AT RISK is still undefined, so it renders "—", never 0: a zero would
// read as "none at risk", which we cannot claim. Separate flags precisely so the two could land
// separately, which is what happened.
const AT_RISK_RULE_DEFINED = false;
const BREACH_RULE_DEFINED = true;

// About to miss its SLA, but hasn't yet.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isAtRisk = (_row: LogisticsOrderRow): boolean => false; // TODO: the ops condition

// Already missed it, AND it is the transporter's to answer for — a shipment forgiven by an
// escalation reads "excused" and is not counted here. One call, shared with the Breach column on the
// Trips tab, so a tile's count and the reason on the row behind it can never tell different stories.
export const isBreached = (row: LogisticsOrderRow): boolean => breachOf(row).state === "breached";

export const slaRuleDefined = (sla: SlaSlice) =>
  sla === "at_risk" ? AT_RISK_RULE_DEFINED : BREACH_RULE_DEFINED;

// --- Slicing --------------------------------------------------------------------------------
const byMetric = (rows: LogisticsOrderRow[], metric: SummaryMetric) => {
  if (metric === "delivered") return rows.filter(isDelivered);
  if (metric === "incomplete") return rows.filter((r) => !isDelivered(r));
  return rows;
};

// The rows behind ANY figure on this tab — the one place the tile counts and the drawer's list are
// derived from, so a tile can never show a number the drawer then contradicts.
export const rowsForRequest = (
  rows: LogisticsOrderRow[],
  req: SummaryRequest
): LogisticsOrderRow[] => {
  let out = byMetric(rows, req.metric);
  if (req.mile === "first_mile") out = out.filter(isFirstMile);
  if (req.mile === "last_mile") out = out.filter((r) => !isFirstMile(r));
  if (req.sla === "at_risk") out = out.filter(isAtRisk);
  if (req.sla === "breached") out = out.filter(isBreached);
  return out;
};

export const SUMMARY_TITLE: Record<SummaryMetric, string> = {
  shipments: "Overall shipments",
  delivered: "Delivered",
  incomplete: "Incomplete",
};

// The transporter's words, not the board's: a vendor thinks in PICK (they collect) and DELIVER
// (they drop). The keys stay `first_mile` / `last_mile` — that is the server's vocabulary.
export const MILE_TITLE: Record<MileSlice, string> = {
  first_mile: "Pick",
  last_mile: "Deliver",
};

export const SLA_TITLE: Record<SlaSlice, string> = {
  at_risk: "At risk",
  breached: "Breached",
};

// The drawer's heading — the slice, then whatever narrowed it.
export const requestTitle = (req: SummaryRequest): string =>
  [SUMMARY_TITLE[req.metric], req.mile && MILE_TITLE[req.mile], req.sla && SLA_TITLE[req.sla]]
    .filter(Boolean)
    .join(" · ");

const HELP: Record<SummaryMetric, string> = {
  shipments:
    "Every shipment in the current filters — one per truck movement, so a consolidated truck carrying several POs counts once.",
  delivered: "Shipments whose delivery status is Delivered.",
  incomplete: "Not yet delivered — created, booked, in transit or attempted.",
};

const MILE_HELP: Record<MileSlice, string> = {
  first_mile: "Pick — goods collected and moving towards our warehouse (supplier pickups, warehouse transfers).",
  last_mile: "Deliver — the leg that carries the goods to the customer.",
};

const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

interface Props {
  // Every figure here is counted SERVER-side, across the whole filtered range — not over the page
  // of trips on screen. A stat that moved as you paged would be worse than no stat.
  stats?: CarrierSummaryResponse;
  drivers: DriverPerformance[];
  tripCount: number; // trips those shipments are spread across
  // Whether the dispatch filter spans more than one day. Only the Driver performance section reads
  // it: punctuality is the one figure here whose UNIT changes with the range (drivers over one day,
  // driver-days over several) — every other tile counts shipments however wide the window is.
  multiDay: boolean;
  loading: boolean;
  onOpen: (request: SummaryRequest) => void;
  onOpenDrivers: () => void;
}

const VendorSummary: React.FC<Props> = ({
  stats,
  drivers,
  tripCount,
  multiDay,
  loading,
  onOpen,
  onOpenDrivers,
}) => {
  const total = stats?.total ?? 0;
  const delivered = stats?.delivered ?? 0;

  const open = (req: SummaryRequest) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen(req);
  };

  // --- the repeated block: mile bar + legend, then the SLA footer -----------------------------
  const mileBreakdown = (metric: SummaryMetric) => {
    const split = stats?.mile?.[metric] ?? { first_mile: 0, last_mile: 0 };
    const first = split.first_mile;
    const last = split.last_mile;
    const sliceTotal = first + last;

    // The MARK is a thin 8px fill; the hit area around it is 24px, so a segment is clickable
    // without drawing a fat bar. A zero half renders nothing rather than a sliver.
    const segment = (mile: MileSlice, count: number, cls: string) =>
      count > 0 ? (
        <Tooltip
          title={`${MILE_TITLE[mile]} — ${count} of ${sliceTotal} (${pct(count, sliceTotal)}%)`}
        >
          <div
            className={styles.segHit}
            style={{ flexGrow: count }}
            role="button"
            onClick={open({ metric, mile })}
          >
            <div className={`${styles.seg} ${cls}`} />
          </div>
        </Tooltip>
      ) : null;

    const legendItem = (mile: MileSlice, count: number, dotCls: string) => (
      <Tooltip title={MILE_HELP[mile]}>
        <span
          className={styles.legendItem}
          role="button"
          tabIndex={0}
          onClick={open({ metric, mile })}
        >
          <span className={`${styles.dot} ${dotCls}`} />
          {MILE_TITLE[mile]}
          <span className={styles.legendValue}>{count}</span>
        </span>
      </Tooltip>
    );

    return (
      <>
        {sliceTotal === 0 ? (
          <div className={styles.barEmpty} />
        ) : (
          <div className={styles.barRow}>
            {segment("first_mile", first, styles.segFirstMile)}
            {segment("last_mile", last, styles.segLastMile)}
          </div>
        )}
        <div className={styles.legend}>
          {legendItem("first_mile", first, styles.dotFirstMile)}
          {legendItem("last_mile", last, styles.dotLastMile)}
        </div>
      </>
    );
  };

  // At risk | Breached, in the order they escalate. A cell is clickable once its rule exists;
  // while it is undefined the dash is inert, because the list behind it would be empty and an
  // empty list would contradict the "unknown" the dash is stating.
  const slaCell = (metric: SummaryMetric, sla: SlaSlice, valueCls: string, noun: string) => {
    const known = slaRuleDefined(sla);
    const n = known ? stats?.breached?.[metric] ?? 0 : null;
    return (
      <Tooltip
        title={
          known
            ? `${n} of these ${noun} are ${SLA_TITLE[sla].toLowerCase()}${
                sla === "breached"
                  ? " and charged to the transporter; ones excused by an escalation are left out"
                  : ""
              } — click to list them.`
            : `${SLA_TITLE[sla]} ${noun} — this condition is not defined yet.`
        }
      >
        <span
          className={`${styles.slaCell} ${known ? styles.slaClickable : ""}`}
          role={known ? "button" : undefined}
          tabIndex={known ? 0 : undefined}
          onClick={known ? open({ metric, sla }) : undefined}
        >
          <span>{SLA_TITLE[sla]}</span>
          <span className={n === null ? styles.slaUnknown : valueCls}>{n === null ? "—" : n}</span>
        </span>
      </Tooltip>
    );
  };

  const slaRow = (metric: SummaryMetric, noun: string) => (
    <div className={styles.slaRow}>
      {slaCell(metric, "at_risk", styles.slaAtRisk, noun)}
      {slaCell(metric, "breached", styles.slaBreached, noun)}
    </div>
  );

  // One tile, one shape — which is what makes the three line up row for row.
  const tile = (
    metric: SummaryMetric,
    value: number,
    sub: React.ReactNode,
    noun: string,
    icon?: React.ReactNode
  ) => {
    return (
      <Col xs={24} lg={8} className={styles.col}>
        <div
          className={`${styles.tile} ${styles.clickable}`}
          role="button"
          onClick={() => onOpen({ metric })}
        >
          <Tooltip title={HELP[metric]}>
            <div className={styles.label}>
              {icon}
              {SUMMARY_TITLE[metric]}
            </div>
          </Tooltip>
          <div className={styles.value}>{value}</div>
          <div className={styles.share}>{sub}</div>
          {mileBreakdown(metric)}
          {/* Pushed to the foot of the tile, so the three footers align however tall the rest runs. */}
          <div className={styles.footer}>{slaRow(metric, noun)}</div>
        </div>
      </Col>
    );
  };

  return (
    <>
      <Row gutter={16} style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.15s ease" }}>
        {tile(
          "shipments",
          total,
          `across ${tripCount} trip${tripCount === 1 ? "" : "s"}`,
          "shipments"
        )}
        {tile(
          "delivered",
          delivered,
          `${pct(delivered, total)}% of ${total} shipment${total === 1 ? "" : "s"}`,
          "deliveries",
          <CheckCircleFilled className={styles.iconGood} />
        )}
        {tile(
          "incomplete",
          total - delivered,
          `${pct(total - delivered, total)}% still to deliver`,
          "shipments",
          <ClockCircleFilled className={styles.iconPending} />
        )}
      </Row>
      <DriverPerformanceSection
        drivers={drivers}
        multiDay={multiDay}
        loading={loading}
        onOpen={onOpenDrivers}
      />
    </>
  );
};

export default VendorSummary;
